import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getBspConversations, getBspConversationMessages, replyToWhatsAppChat, setWhatsAppChatHandler } from '../services/tenant.service';
import { useAuthStore } from '../store/authStore';

const button = 'min-h-[44px] rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium disabled:opacity-50';
const senders = { customer: 'Customer', agent: 'AI agent', human: 'Your team', system: 'System' };
const stamp = (m) => m.occurred_at || m.created_at;
const errorText = (e, fallback) => typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : fallback;
function mergeMessages(old, incoming) {
  const rows = new Map(old.map((m) => [m.id, m]));
  incoming.forEach((m) => rows.set(m.id, m));
  return [...rows.values()].sort((a, b) => new Date(stamp(a)) - new Date(stamp(b)) || a.id.localeCompare(b.id));
}

function Inbox({ tenantId, channel, setChannel, canConfigure }) {
  const [conversations, setConversations] = useState([]);
  const [total, setTotal] = useState(0);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState('');
  const [history, setHistory] = useState({});
  const [messageErrors, setMessageErrors] = useState({});
  const [loadingMessages, setLoadingMessages] = useState('');
  const [drafts, setDrafts] = useState({});
  const [acting, setActing] = useState('');
  const live = useRef(true);
  const fetching = useRef(false);
  const firstLoad = useRef(true);
  const expanded = useRef('');
  expanded.current = open;

  const readMessages = useCallback(async (id, before = null) => {
    setLoadingMessages(id);
    setMessageErrors((p) => ({ ...p, [id]: '' }));
    try {
      const data = await getBspConversationMessages(tenantId, id, { limit: 100, newest: true, ...(before ? { before } : {}) });
      if (!live.current) return;
      setHistory((p) => ({ ...p, [id]: {
        messages: mergeMessages(p[id]?.messages || [], data.messages || []),
        cursor: before || !p[id] ? data.next_cursor : p[id].cursor,
      } }));
    } catch (e) {
      if (live.current) setMessageErrors((p) => ({ ...p, [id]: errorText(e, 'Could not load messages. Try again.') }));
    } finally {
      if (live.current) setLoadingMessages('');
    }
  }, [tenantId]);

  const load = useCallback(async (before = null) => {
    if (!tenantId || fetching.current) return;
    fetching.current = true;
    setRefreshing(true);
    setError('');
    try {
      const data = await getBspConversations(tenantId, { limit: 50, ...(channel ? { channel } : {}), ...(before ? { before } : {}) });
      if (!live.current) return;
      setConversations((p) => {
        const rows = new Map(p.map((c) => [c.id, c]));
        (data.conversations || []).forEach((c) => rows.set(c.id, c));
        return [...rows.values()].sort((a, b) =>
          new Date(b.updated_at || b.last_message_at) - new Date(a.updated_at || a.last_message_at) || b.id.localeCompare(a.id));
      });
      setTotal(data.total || 0);
      if (before || firstLoad.current) setCursor(data.next_cursor);
      firstLoad.current = false;
      if (!before && expanded.current) await readMessages(expanded.current);
    } catch (e) {
      if (live.current) setError(errorText(e, 'Could not load conversations. Try again.'));
    } finally {
      fetching.current = false;
      if (live.current) { setLoading(false); setRefreshing(false); }
    }
  }, [tenantId, channel, readMessages]);

  useEffect(() => {
    live.current = true;
    load();
    const timer = setInterval(() => load(), 30000);
    return () => { live.current = false; clearInterval(timer); };
  }, [load]);

  const act = async (c, handler) => {
    setActing(c.id);
    try {
      if (handler) {
        const result = await setWhatsAppChatHandler(tenantId, c.id, handler);
        if (live.current) setConversations((p) => p.map((row) => row.id === c.id ? { ...row, handled_by: result.handled_by } : row));
      } else {
        await replyToWhatsAppChat(tenantId, c.id, (drafts[c.id] || '').trim());
        if (!live.current) return;
        setDrafts((p) => ({ ...p, [c.id]: '' }));
        setConversations((p) => p.map((row) => row.id === c.id ? { ...row, handled_by: 'team' } : row));
        await readMessages(c.id);
      }
    } catch (e) {
      if (live.current) toast.error(errorText(e, handler ? 'Could not update this chat.' : 'Could not send your reply.'));
    } finally {
      if (live.current) setActing('');
    }
  };

  return (
    <div className="mx-auto max-w-5xl p-4 pb-24 sm:p-6 sm:pb-24">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="text-xl font-bold text-gray-900">Customer inbox</h1>
          <p className="mt-1 text-sm text-gray-600">Customer messages from WhatsApp and Instagram.</p></div>
        <div className="flex flex-wrap gap-2">
          {canConfigure && <Link className={button} to={`/t/${tenantId}/whatsapp-outbound`}>Channels</Link>}
          <button className={button} disabled={refreshing} onClick={() => load()}>{refreshing ? 'Refreshing…' : 'Refresh'}</button>
        </div>
      </header>
      <label className="mb-5 block text-sm font-medium text-gray-700">Channel
        <select className="ml-3 min-h-[44px] rounded-lg border border-gray-300 px-3" value={channel} onChange={(e) => setChannel(e.target.value)}>
          <option value="">All channels</option><option value="whatsapp">WhatsApp</option><option value="instagram">Instagram</option>
        </select>
      </label>
      {error && <p role="alert" className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {loading && <p role="status">Loading conversations…</p>}
      {!loading && !error && conversations.length === 0 && <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center">
        <h2 className="font-semibold text-gray-900">No conversations yet</h2>
        <p className="mt-2 text-sm text-gray-600">{channel
          ? 'No messages on this channel yet. Try All channels or send a test message to your connected account.'
          : 'Already connected? Send a message to your business account from another account to test it.'}</p>
        {canConfigure ? <Link className={`mt-4 inline-flex ${button}`} to={`/t/${tenantId}/whatsapp-outbound`}>Check channel setup</Link>
          : <p className="mt-2 text-sm text-gray-600">Ask your workspace admin to check Channels if messages are missing.</p>}
      </div>}
      <div className="space-y-4">{conversations.map((c) => {
        const name = c.contact_name || c.contact_phone || 'Unknown contact';
        const messages = history[c.id]?.messages || [];
        const isAI = c.handled_by === 'ai';
        return <article key={c.id} aria-label={`Conversation with ${name}`} className="min-w-0 rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-semibold text-gray-900">{name}</h2>
            <span className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-700">{c.channel === 'instagram' ? 'Instagram' : 'WhatsApp'}</span>
            {c.handled_by && <span className="rounded-full bg-indigo-50 px-2 py-1 text-xs text-indigo-700">{isAI ? 'AI answering' : 'Your team'}</span>}
          </div>
          {c.contact_phone && <p className="mt-1 break-words text-sm text-gray-600">{c.contact_phone}</p>}
          {c.agent_name && <p className="text-sm text-gray-600">Agent: {c.agent_name}</p>}
          {(c.campaign_source || c.campaign_name) && <p className="text-sm text-gray-600">
            Campaign: {[c.campaign_source, c.campaign_name].filter(Boolean).join(' / ')}
          </p>}
          {c.last_message_at && <p className="mt-1 text-xs text-gray-500">Last message: {new Date(c.last_message_at).toLocaleString()}</p>}
          {c.reply_unavailable_reason && <p className="mt-2 text-sm text-gray-600">{c.reply_unavailable_reason}</p>}
          <div className="mt-3 flex flex-wrap gap-2">
            <button className={button} onClick={() => { setOpen(open === c.id ? '' : c.id); if (open !== c.id) readMessages(c.id); }}>
              {open === c.id ? 'Hide messages' : 'View messages'}</button>
            {(isAI ? c.can_take_over : c.can_hand_back) && <button className={button} disabled={acting === c.id}
              onClick={() => act(c, isAI ? 'team' : 'ai')}>{isAI ? 'Take over from AI' : 'Hand back to AI'}</button>}
          </div>
          {open === c.id && <div className="mt-3 space-y-3 rounded-lg bg-gray-50 p-3">
            {history[c.id]?.cursor && <button className={button} disabled={loadingMessages === c.id}
              onClick={() => readMessages(c.id, history[c.id].cursor)}>Load earlier messages</button>}
            {loadingMessages === c.id && <p role="status" className="text-sm">Loading messages…</p>}
            {messageErrors[c.id] && <div role="alert"><p className="text-sm text-red-700">{messageErrors[c.id]}</p>
              <button className={button} onClick={() => readMessages(c.id)}>Retry messages</button></div>}
            {messages.map((m) => <div key={m.id} className="rounded-lg border border-gray-200 bg-white p-3">
              <div className="flex flex-wrap justify-between gap-2 text-xs text-gray-600">
                <span className="font-semibold">{senders[m.sender_type] || m.sender_type || 'Message'}</span>
                <time>{stamp(m) ? new Date(stamp(m)).toLocaleString() : ''}</time>
              </div><p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-800">{m.content || 'Non-text message'}</p>
            </div>)}
            {!loadingMessages && !messageErrors[c.id] && messages.length === 0 && <p className="text-sm">No messages yet.</p>}
            {c.can_reply && <form onSubmit={(e) => { e.preventDefault(); act(c); }}>
              <label htmlFor={`reply-${c.id}`} className="block text-sm font-medium">Reply to {name}</label>
              <textarea id={`reply-${c.id}`} rows={3} maxLength={4096} className="mt-2 block w-full rounded-lg border border-gray-300 p-3 text-base"
                value={drafts[c.id] || ''} onChange={(e) => setDrafts((p) => ({ ...p, [c.id]: e.target.value }))} />
              <p className="my-2 text-xs text-gray-600">Sending pauses AI replies in this chat for 12 hours.</p>
              <button type="submit" className={`${button} bg-green-700 text-white`}
                disabled={acting === c.id || !(drafts[c.id] || '').trim()}>Send</button>
            </form>}
          </div>}
        </article>;
      })}</div>
      {!loading && conversations.length > 0 && <footer className="mt-5 text-center">
        <p className="mb-3 text-sm text-gray-600">Showing {conversations.length} of {total} conversations. Refreshes every 30 seconds.</p>
        {cursor && <button className={button} disabled={refreshing} onClick={() => load(cursor)}>Load more conversations</button>}
      </footer>}
    </div>
  );
}

export default function WhatsAppInboxPage() {
  const { tenantId: urlTenantId } = useParams();
  const user = useAuthStore((s) => s.user);
  const tenantId = urlTenantId || user?.tenant_id;
  const [channel, setChannel] = useState('');
  const canConfigure = user?.is_superuser || user?.role === 'superadmin' || user?.role === 'admin'
    || ['admin', 'owner'].includes(user?.tenant_role);
  // A new scope remounts the inbox so late responses cannot cross workspaces.
  return <Inbox key={`${tenantId}:${channel}`} tenantId={tenantId} channel={channel} setChannel={setChannel} canConfigure={canConfigure} />;
}
